import React, { useContext, useEffect, useState } from 'react';
import { API } from 'aws-amplify';

import AppContext from '../../AppContext';
import QuickSightChatAgent from '../QuickSightChatAgent/QuickSightChatAgent';

const ChatEmbed = ({
  agentId,
  className = '',
  onLoadingChange,
  onChatReady
}) => {
  const {
    apiKey,
    embedContext,
    setDashboard
  } = useContext(AppContext);

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Sync loading state up to the parent (e.g. chat header status).
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Previously tried to suppress QuickSight auto-scrolling the page while
  // the chat connects, through several different approaches -- none of
  // them actually held up under real testing, so this is now left alone:
  // whatever scroll happens while the chat loads is allowed to happen.

  useEffect(() => {
    if (!agentId || !embedContext) {
      return;
    }

    // Hide the overlay at FRAME_LOADED rather than waiting for full
    // EXPERIENCE_INITIALIZED -- feels snappier. MAX_WAIT_MS is a fallback.
    let isActive = true;
    const MAX_WAIT_MS = 15000;

    const loadChatAgent = async () => {
      try {
        setErrorMessage('');
        setIsLoading(true);
        setDashboard();
        onChatReady?.(null);

        const usesSharedChatUser =
          import.meta.env.VITE_ANONYMOUS === 'true' || !!apiKey;

        const endpoint = usesSharedChatUser
          ? `/get-shared-quickchat-url?agent_id=${encodeURIComponent(agentId)}&hostname=${encodeURIComponent(window.location.origin)}`
          : `/get-quicksight-url?dashboard_id=${encodeURIComponent(agentId)}&hostname=${encodeURIComponent(window.location.origin)}&experience=quickchat`;

        const response = await API.get(
          'APIEndpoint',
          endpoint,
          {
            response: true,
            headers: apiKey
              ? { 'X-API-Key': apiKey }
              : undefined
          }
        );

        if (!response?.data?.url) {
          setErrorMessage(
            'Unable to get QuickSight chat embed URL.'
          );
          return;
        }

        const { embedQuickChat } = embedContext;

        let resolveFrameLoaded;
        const frameLoaded = new Promise((resolve) => {
          resolveFrameLoaded = resolve;
        });
        const frameLoadTimer = setTimeout(resolveFrameLoaded, MAX_WAIT_MS);

        const frameOptions = {
          url: response.data.url,
          container: document.getElementById(
            `quick-chat-${agentId}`
          ),
          height: '100%',
          width: '100%',
          onChange: (changeEvent) => {
            if (changeEvent.eventName === 'FRAME_LOADED') {
              clearTimeout(frameLoadTimer);
              resolveFrameLoaded();
            }
          }
        };

        const contentOptions = {
          agentOptions: {
            fixedAgentId: agentId
          },
          promptOptions: {
            allowFileAttachments: false,
            showAgentKnowledgeBoundary: false,
            enablePrivateMode: false,
            showWebSearch: false,
            showChatHistory: false,
            // Deliberately NOT setting `initialPrompt` here. Per AWS's own
            // docs it "will be sent once on initial chat panel load"
            // regardless of showInitialPromptMessage -- that flag only
            // controls whether the resulting message bubble is shown, not
            // whether it's sent. So it fired a full live BidFlow run
            // (document classification, cost waterfall, Excel generation)
            // for every single visitor on page load, just hidden when the
            // flag was false, which looked like nothing was happening
            // while a run was still going in the background. Instead, the
            // suggested prompt lives in the "Try asking" list next to the
            // chat (see VideoChatStarterLayout) as a button that calls
            // this instance's own `sendPrompt(text)` method on click --
            // same live run, but only once a visitor actually asks for it.
          }
        };

        const embeddedChat = await embedQuickChat(
          frameOptions,
          contentOptions
        );

        setDashboard(embeddedChat);

        // Hand the live instance up to the parent so it can drive the chat
        // from outside the iframe -- e.g. a "Try asking" suggestion button
        // that calls embeddedChat.sendPrompt(text) on click, instead of
        // the visitor having to copy/paste it in themselves.
        onChatReady?.(embeddedChat);

        await frameLoaded;

      } catch (error) {
        console.error(
          'Error loading QuickSight chat agent:',
          error
        );

        setErrorMessage(
          'Unable to load the chat agent.'
        );
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadChatAgent();

    return () => {
      isActive = false;
    };

  }, [
    agentId,
    apiKey,
    embedContext,
    setDashboard
  ]);

  if (!agentId) {
    return null;
  }

  return (
    <div className={className}>
      <QuickSightChatAgent
        agentId={agentId}
        errorMessage={errorMessage}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ChatEmbed;
